/**
 * What Bullhorn already holds about a candidate, for the screen that asks them to confirm it.
 *
 * **The returning screen was asking, not confirming.** KID-2's lookup returns a diagnosis and an id, not a
 * record, so every field below the four check-in factors opened blank — while the screen's own heading said
 * «We've filled in what's already on file — just review and edit anything that's changed». Somebody who had
 * completed this on a previous visit was asked to type it again.
 *
 * Fails OPEN, deliberately: an unreadable profile yields an empty form, never an error screen. The candidate
 * can type their details, and a kiosk that refused to let them because a read timed out is the dead end
 * BR-K7 forbids.
 */

import { kioskFetch } from './kioskFetch';
export interface CandidateProfile {
  readonly firstName?: string;
  readonly lastName?: string;
  readonly email?: string;
  readonly phone?: string;
  readonly city?: string;
  readonly state?: string;
  readonly zip?: string;
  /** Hourly. Never annual [decision 0043]. */
  readonly payRate?: string;
  /** ISO date. Availability, never a date of birth. */
  readonly startDate?: string;
  /** One of Bullhorn's Shift Type values, or absent. */
  readonly shiftPreference?: string;
  /** The street line. City, state and ZIP are separate. */
  readonly homeAddress?: string;
  readonly emergencyContact?: string;
  readonly emergencyPhone?: string;
  /**
   * Whether the record already holds the candidate's consent to contact [0507].
   *
   * Only `true` skips the consent modal on the returning screen. `null` (the api could not tell) and absent
   * (an older api, or a read that failed) read as `false` and the modal is asked, because asking a candidate
   * once too often costs a tap and not asking costs ISS the right to contact them [0160].
   */
  readonly consentOnFile?: boolean | null;
  /**
   * Whether the I-9 step still applies.
   *
   * A state, not ISS's raw compliance flag — the api reduces it before it crosses the wire, because a lobby
   * screen that rendered a compliance status would disclose it to whoever is standing behind the candidate.
   */
  readonly i9: 'complete' | 'outstanding' | 'unknown';
}

const REQUEST_TIMEOUT_MS = 10_000;

function apiBaseUrl(): string {
  return import.meta.env.VITE_API_BASE_URL ?? '';
}

export async function fetchCandidateProfile(candidateId: number): Promise<CandidateProfile> {
  try {
    const response = await kioskFetch(`${apiBaseUrl()}/candidates/${candidateId}/profile`, {
      signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    });
    if (!response.ok) return { i9: 'unknown' };
    return (await response.json()) as CandidateProfile;
  } catch {
    // `unknown` rather than a thrown error: the caller offers every step, which is the safe side.
    return { i9: 'unknown' };
  }
}
